function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { BufferEncoders, MESSAGE_RSOCKET_COMPOSITE_METADATA, RSocketClient, createBuffer, toBuffer } from 'rsocket-core';
import RSocketWebSocketClient from 'rsocket-websocket-client';
import { createMetadata, createSetupMetadata } from '@edusense/entities-communication';
/**
 * Frontend RSocket client wrapper that manages a single connection,
 * exposes helper methods for fire-and-forget sends and request-stream subscriptions,
 * and performs automatic reconnects when the transport is interrupted.
 */ let RSocketChatClient = class RSocketChatClient {
    async connect() {
        if (this.rsocket) {
            return;
        }
        if (this.connectPromise) {
            return this.connectPromise;
        }
        this.shouldReconnect = true;
        this.connectPromise = new Promise((resolve, reject)=>{
            const client = this.createClient();
            client.connect().subscribe({
                onComplete: (socket)=>{
                    console.log('[RSocket] Connected successfully');
                    this.rsocket = socket;
                    this.reconnectAttempts = 0;
                    socket.connectionStatus().subscribe({
                        onNext: (status)=>{
                            if (status.kind === 'ERROR' || status.kind === 'CLOSED') {
                                this.handleDisconnect();
                            }
                        }
                    });
                    resolve();
                },
                onError: (error)=>{
                    console.error('[RSocket] Failed to connect:', error);
                    this.handleDisconnect();
                    reject(error);
                }
            });
        });
        return this.connectPromise;
    }
    sendMessage(roomId, message) {
        const socket = this.assertConnected();
        const metadata = this.createRouteMetadata(`api.v1.messages.stream.${roomId}`);
        try {
            const payload = toJsonBuffer(message);
            socket.fireAndForget({
                data: payload,
                metadata
            });
            console.log('[RSocket] Message send dispatched');
        } catch (error) {
            console.error('[RSocket] Failed to send message:', error);
            throw error;
        }
    }
    async requestResponse(route, payload) {
        const socket = this.assertConnected();
        const metadata = this.createRouteMetadata(route);
        const dataBuffer = toJsonBuffer(payload ?? {});
        console.log('[RSocket] requestResponse start', route, payload);
        return await new Promise((resolve, reject)=>{
            socket.requestResponse({
                data: dataBuffer,
                metadata
            }).subscribe({
                onComplete: (response)=>{
                    console.log('[RSocket] requestResponse onComplete', route);
                    if (!response || !response.data || response.data.length === 0) {
                        resolve({});
                        return;
                    }
                    try {
                        const parsed = JSON.parse(response.data.toString());
                        resolve(parsed);
                    } catch (error) {
                        console.error('[RSocket] requestResponse parse error', route, error);
                        reject(error);
                    }
                },
                onError: (error)=>{
                    console.error('[RSocket] requestResponse error', route, error);
                    reject(error);
                }
            });
        });
    }
    subscribeToMessages(roomId, onMessage) {
        const socket = this.assertConnected();
        const metadata = this.createRouteMetadata(`api.v1.messages.stream.${roomId}`);
        let activeSubscription = null;
        socket.requestStream({
            data: this.emptyPayload,
            metadata
        }).subscribe({
            onSubscribe: (subscription)=>{
                activeSubscription = subscription;
                subscription.request(1000);
            },
            onNext: (payload)=>{
                try {
                    const message = JSON.parse(payload.data.toString());
                    onMessage(message);
                } catch (error) {
                    console.error('[RSocket] Failed to parse message payload:', error);
                }
            },
            onError: (error)=>{
                console.error('[RSocket] Message subscription error:', error);
            }
        });
        return ()=>{
            activeSubscription?.cancel?.();
            activeSubscription = null;
        };
    }
    subscribeToRoomInfos(userId, onRoomInfo) {
        const socket = this.assertConnected();
        const metadata = this.createRouteMetadata(`api.v1.messages.threadInfos.${userId}`);
        let activeSubscription = null;
        socket.requestStream({
            data: this.emptyPayload,
            metadata
        }).subscribe({
            onSubscribe: (subscription)=>{
                activeSubscription = subscription;
                subscription.request(1000);
            },
            onNext: (payload)=>{
                try {
                    const roomInfos = JSON.parse(payload.data.toString());
                    onRoomInfo(roomInfos);
                } catch (error) {
                    console.error('[RSocket] Failed to parse room info payload:', error);
                }
            },
            onError: (error)=>{
                console.error('[RSocket] Room info subscription error:', error);
            }
        });
        return ()=>{
            activeSubscription?.cancel?.();
            activeSubscription = null;
        };
    }
    subscribeToLastReadTimes(roomId, onLastReadTimes) {
        const socket = this.assertConnected();
        const metadata = this.createRouteMetadata(`api.v1.messages.lastReadTimes.${roomId}`);
        let activeSubscription = null;
        socket.requestStream({
            data: this.emptyPayload,
            metadata
        }).subscribe({
            onSubscribe: (subscription)=>{
                activeSubscription = subscription;
                subscription.request(1000);
            },
            onNext: (payload)=>{
                try {
                    const times = JSON.parse(payload.data.toString());
                    onLastReadTimes(times);
                } catch (error) {
                    console.error('[RSocket] Failed to parse last read payload:', error);
                }
            },
            onError: (error)=>{
                console.error('[RSocket] Last read times subscription error:', error);
            }
        });
        return ()=>{
            activeSubscription?.cancel?.();
            activeSubscription = null;
        };
    }
    async connectToRoom(roomId) {
        const socket = this.assertConnected();
        const metadata = this.createRouteMetadata(`api.v1.messages.connect.${roomId}`);
        let activeSubscription = null;
        socket.requestStream({
            data: this.emptyPayload,
            metadata
        }).subscribe({
            onSubscribe: (subscription)=>{
                activeSubscription = subscription;
                subscription.request(1000);
            },
            onError: (error)=>{
                console.error('[RSocket] Room connection error:', error);
            }
        });
        return ()=>{
            activeSubscription?.cancel?.();
            activeSubscription = null;
        };
    }
    disconnect() {
        this.shouldReconnect = false;
        if (this.rsocket) {
            try {
                this.rsocket.close();
            } catch (error) {
                console.warn('[RSocket] Error while closing connection:', error);
            }
        }
        this.rsocket = null;
        this.connectPromise = null;
    }
    createClient() {
        return new RSocketClient({
            setup: {
                keepAlive: 60000,
                lifetime: 180000,
                dataMimeType: 'application/json',
                metadataMimeType: MESSAGE_RSOCKET_COMPOSITE_METADATA.string,
                metadata: createSetupMetadata(this.token)
            },
            transport: new RSocketWebSocketClient({
                url: this.url
            }, BufferEncoders)
        });
    }
    createRouteMetadata(route) {
        return createMetadata(route, this.token);
    }
    assertConnected() {
        if (!this.rsocket) {
            throw new Error('RSocket not connected');
        }
        return this.rsocket;
    }
    handleDisconnect() {
        this.rsocket = null;
        this.connectPromise = null;
        if (!this.shouldReconnect) {
            return;
        }
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error('[RSocket] Max reconnection attempts reached');
            return;
        }
        this.reconnectAttempts += 1;
        const delay = this.reconnectBaseDelayMs * this.reconnectAttempts;
        console.log(`[RSocket] Attempting reconnect #${this.reconnectAttempts} in ${delay}ms`);
        setTimeout(()=>{
            this.connect().catch((error)=>{
                console.error('[RSocket] Reconnect attempt failed:', error);
            });
        }, delay);
    }
    constructor(url, token){
        _define_property(this, "url", void 0);
        _define_property(this, "token", void 0);
        _define_property(this, "rsocket", void 0);
        _define_property(this, "connectPromise", void 0);
        _define_property(this, "shouldReconnect", void 0);
        _define_property(this, "reconnectAttempts", void 0);
        _define_property(this, "maxReconnectAttempts", void 0);
        _define_property(this, "reconnectBaseDelayMs", void 0);
        _define_property(this, "emptyPayload", void 0);
        this.url = url;
        this.token = token;
        this.rsocket = null;
        this.connectPromise = null;
        this.shouldReconnect = true;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectBaseDelayMs = 1000;
        this.emptyPayload = createBuffer(0);
    }
};
export default RSocketChatClient;
function toJsonBuffer(payload) {
    if (isBuffer(payload)) {
        return payload;
    }
    if (payload instanceof Uint8Array) {
        return toBuffer(payload);
    }
    if (typeof payload === 'string') {
        return toBuffer(payload, 'utf8');
    }
    return toBuffer(JSON.stringify(payload ?? {}), 'utf8');
}
function isBuffer(value) {
    return !!value && typeof value.copy === 'function' && typeof value.length === 'number';
}
